import { canonicalizeEventIds, chunkEventIds, type LiveEventSnapshot } from "./snapshots";

/**
 * Browser-side client for `GET /api/live/events`.
 *
 * Ids are canonicalised (sorted, de-duplicated) and split into fixed chunks
 * of MAX_EVENT_IDS so a large set never produces an oversized URL; chunks are
 * fetched in parallel and merged. Because the split is deterministic, every
 * viewer with the same set hits the same edge cache entries. Pages that poll
 * hundreds of matches should use a selector endpoint (by date / competition)
 * instead of listing ids at all.
 *
 * Resolves to whatever snapshots could be fetched; a failed chunk is dropped
 * and pollers keep showing the last snapshot they have.
 */
export async function fetchLiveEventSnapshots(eventIds: readonly string[]): Promise<LiveEventSnapshot[]> {
    const ids = canonicalizeEventIds(eventIds);
    if (ids.length === 0) return [];
    const results = await Promise.all(chunkEventIds(ids).map(fetchChunk));
    return results.flat();
}

async function fetchChunk(ids: string[]): Promise<LiveEventSnapshot[]> {
    try {
        // no-store bypasses the browser's own HTTP cache only; the request still
        // reaches Cloudflare, which serves its 15 s edge copy. Without it the
        // zone's Browser Cache TTL (max-age=14400 in prod) would let the browser
        // answer every poll from its cache for four hours.
        const response = await fetch(`/api/live/events?ids=${encodeURIComponent(ids.join(","))}`, {
            headers: { Accept: "application/json" },
            cache: "no-store",
        });
        if (!response.ok) return [];
        const body = (await response.json()) as { events?: LiveEventSnapshot[] };
        return Array.isArray(body.events) ? body.events : [];
    } catch {
        return [];
    }
}
